<script lang="ts">
  import { describeUnsupported, type GpuUnsupportedReason } from '$lib/gpu/device'

  interface Props {
    reason: GpuUnsupportedReason
    detail?: string
  }

  const { reason, detail }: Props = $props()
</script>

<section class="gate" data-testid="webgpu-gate">
  <h1>WebGPU is required</h1>
  <p class="lead">{describeUnsupported(reason)}</p>
  <p>
    This vectorizer fits curves to your drawing with a GPU optimization loop, so it needs a browser
    with WebGPU enabled.
  </p>

  <h2>Browsers that work</h2>
  <ul>
    <li>Chrome or Edge 113 and later</li>
    <li>Safari 18 and later (macOS Sequoia, iOS 18)</li>
    <li>Firefox 141 and later on Windows, 142 and later on macOS and Linux</li>
  </ul>

  {#if reason === 'no-adapter'}
    <p class="hint">
      On Linux, WebGPU often needs a working Vulkan driver. On desktop Chrome you can also try
      enabling <code>chrome://flags/#enable-unsafe-webgpu</code>.
    </p>
  {/if}

  {#if detail}
    <pre class="detail">{detail}</pre>
  {/if}
</section>

<style>
  .gate {
    max-width: 42rem;
    margin: 4rem auto;
    padding: 2rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }

  h1 {
    margin-top: 0;
    font-size: 1.5rem;
  }

  h2 {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }

  .lead {
    color: var(--danger);
    font-weight: 500;
  }

  .hint,
  ul {
    color: var(--text-muted);
  }

  code {
    font-family: var(--mono);
    font-size: 0.9em;
    background: var(--surface-raised);
    padding: 0.1em 0.4em;
    border-radius: 4px;
  }

  .detail {
    font-family: var(--mono);
    font-size: 0.8rem;
    color: var(--text-muted);
    background: var(--surface-raised);
    padding: 0.75rem;
    border-radius: 6px;
    overflow-x: auto;
    white-space: pre-wrap;
  }
</style>
